// =============================================================================
// benvel-stylist-flow.js — Epic 18 AI Stylist guided flow asset bundle.
// This file is shared across Epic 18 stories. Each story's contribution is
// enclosed in named delimiters so parallel sibling stories append additively
// without overwriting each other.
//
// Story ownership:
//   18.2 — stylist-intro block (this file initiates the bundle)
//
// Per §D6 of Story 18.2 Dev Notes: append-only, delimited blocks. Never
// overwrite a sibling's block.
// =============================================================================

// === stylist-intro ===

/**
 * benvel-stylist-flow.js — stylist-intro block
 * Story 18.2: AI Stylist intro modal
 *
 * Manages the AI Stylist intro modal lifecycle:
 * - Opens the <dialog> via showModal() on trigger click (AC1)
 * - "Empezar guía" closes + dispatches benvel:stylist-intro-start (AC2)
 * - "Ahora no" closes consent-gated (AC3 — no analytics without consent)
 * - Close button + ESC + scrim click close (AC1, AC7)
 * - Focus restoration handled natively by the browser's <dialog> (AC7)
 * - Duplicate-load guard via data-benvel-stylist-flow sentinel (AC4 §Task 4)
 *
 * No external dependencies. Vanilla JS only.
 * Loaded via <script defer> emitted by snippets/stylist-intro.liquid.
 */
(function () {
  'use strict';

  // Duplicate-load guard — if another sibling story loads this bundle first,
  // the sentinel prevents double-initialization of the intro block.
  if (document.documentElement.dataset.benvelStylistFlowIntro === 'loaded') {
    return;
  }
  document.documentElement.dataset.benvelStylistFlowIntro = 'loaded';

  /**
   * Initialize the intro modal wiring once the DOM is ready.
   */
  function initStylistIntro() {
    var dlg = document.getElementById('benvel-stylist-intro');

    // Early return — modal may not be mounted on every page.
    if (!dlg) return;

    // -------------------------------------------------------------------------
    // Open trigger: any element with [data-stylist-intro-open] on the page
    // -------------------------------------------------------------------------
    document.querySelectorAll('[data-stylist-intro-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        dlg.showModal();
        window.dispatchEvent(new CustomEvent('benvel:stylist-intro-opened'));
      });
    });

    // -------------------------------------------------------------------------
    // Primary CTA — "Empezar guía" (AC2)
    // -------------------------------------------------------------------------
    var startBtn = dlg.querySelector('[data-stylist-intro-start]');
    if (startBtn) {
      startBtn.addEventListener('click', function () {
        dlg.close('start');
        window.dispatchEvent(new CustomEvent('benvel:stylist-intro-start'));
      });
    }

    // -------------------------------------------------------------------------
    // Secondary CTA — "Ahora no" (AC3)
    // Consent gate: only emit analytics event if consent.has('analytics').
    // The forbidden state is emission without consent — silence is the default.
    // -------------------------------------------------------------------------
    var dismissBtn = dlg.querySelector('[data-stylist-intro-dismiss]');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () {
        dlg.close('dismiss');
        // Consent-gated analytics emission (AC3 hard invariant)
        if (
          window.benvel &&
          window.benvel.consent &&
          typeof window.benvel.consent.has === 'function' &&
          window.benvel.consent.has('analytics')
        ) {
          window.dispatchEvent(new CustomEvent('benvel:stylist-intro-dismissed'));
        }
        // If consent is absent: no event fires — silent skip per AC3.
      });
    }

    // -------------------------------------------------------------------------
    // Close button (×) (AC1)
    // -------------------------------------------------------------------------
    var closeBtn = dlg.querySelector('[data-stylist-intro-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        dlg.close('close');
      });
    }

    // -------------------------------------------------------------------------
    // Scrim click — clicking the dialog backdrop area closes the modal (AC1)
    // Clicks on the dialog element itself (not its children) land on the scrim.
    // -------------------------------------------------------------------------
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) {
        dlg.close('scrim');
      }
    });

    // -------------------------------------------------------------------------
    // Focus management on close (AC7)
    // Native <dialog> restores focus to the element that called showModal().
    // No manual restoration needed — the browser handles this.
    // The close event handler is a hook for future story integrations only.
    // -------------------------------------------------------------------------
    dlg.addEventListener('close', function () {
      // Focus restoration is handled natively by the browser.
      // Story 18.3 may listen for 'benvel:stylist-intro-start' on window
      // to advance to Step 1. No additional work needed here.
    });
  }

  // -------------------------------------------------------------------------
  // Boot: initialize when DOM is ready
  // -------------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStylistIntro);
  } else {
    initStylistIntro();
  }
})();

// === /stylist-intro ===

// === stylist-step ===

/**
 * benvel-stylist-flow.js — stylist-step block
 * Story 18.3: Step UI scaffold + chip grid + progress indicator
 *
 * Custom element: <benvel-stylist-flow>
 * Decision §D5: vanilla custom element (NO Stimulus dependency).
 * Precedent: Story 11.1's <benvel-ai-slot> established the vanilla
 * custom-element pattern for this AI namespace.
 *
 * Responsibilities (AC4):
 *   - Listens for chip change events (aria-pressed toggle).
 *   - On Continue: persists current step's selected value(s) to
 *     sessionStorage[SESSION_KEY] (JSON object keyed by question id).
 *   - On Back: rehydrates previous step's chip state from sessionStorage;
 *     decrements currentStep counter.
 *   - On step 0 Back: dispatches 'benvel:stylist-step-close' event so
 *     the parent <dialog> (18.2's intro modal) can respond.
 *   - No-JS fallback: when JS fails, the <form method="post"> wrapper
 *     keeps Continue / Back as native form submits with ?step=NN query
 *     param (D4). Page renders without runtime JS errors.
 *
 * sessionStorage keys:
 *   benvel.stylist.answers     — JSON {[questionId]: selectedValue|string[]}
 *   benvel.stylist.currentStep — integer (1-based)
 *
 * Chip multi-select (D2): default single-select (one aria-pressed="true"
 * per group). If data-multiselect="true" on the chip-group, allows subset.
 */

(function () {
  'use strict';

  // Duplicate-load guard
  if (document.documentElement.dataset.benvelStylistFlowStep === 'loaded') {
    return;
  }
  document.documentElement.dataset.benvelStylistFlowStep = 'loaded';

  /**
   * BenvelStylistFlow custom element.
   * Wraps the entire multi-step guided flow container.
   */
  var BenvelStylistFlow = (function () {
    function BenvelStylistFlow() {}

    BenvelStylistFlow.SESSION_KEY = 'benvel.stylist.answers';
    BenvelStylistFlow.STEP_KEY = 'benvel.stylist.currentStep';

    BenvelStylistFlow.prototype = Object.create(HTMLElement.prototype);
    BenvelStylistFlow.prototype.constructor = BenvelStylistFlow;

    BenvelStylistFlow.prototype.connectedCallback = function () {
      this._rehydrate();
      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('change', this._onChipChange.bind(this));

      // Listen for intro-modal start event (dispatched by 18.2 §D6 JS block)
      window.addEventListener(
        'benvel:stylist-intro-start',
        this._onIntroStart.bind(this)
      );
    };

    /**
     * Rehydrate chip state from sessionStorage on initial load or
     * when the element re-connects after a step transition.
     */
    BenvelStylistFlow.prototype._rehydrate = function () {
      var raw = sessionStorage.getItem(BenvelStylistFlow.SESSION_KEY);
      var answers = {};
      try {
        answers = raw ? JSON.parse(raw) : {};
      } catch (e) {
        answers = {};
      }

      var stepEl = this.querySelector('[data-stylist-step]');
      if (!stepEl) return;

      var questionId = stepEl.dataset.questionId;
      var savedValue = answers[questionId];
      if (!savedValue) return;

      var chipGroup = stepEl.querySelector('[data-chip-group]');
      if (!chipGroup) return;

      var isMulti = chipGroup.dataset.multiselect === 'true';
      var chips = chipGroup.querySelectorAll('.benvel-filter-chip');

      chips.forEach(function (chip) {
        var chipValue = chip.dataset.filterValue;
        if (isMulti) {
          var selected = Array.isArray(savedValue)
            ? savedValue.indexOf(chipValue) !== -1
            : savedValue === chipValue;
          chip.setAttribute('aria-pressed', selected ? 'true' : 'false');
        } else {
          chip.setAttribute(
            'aria-pressed',
            chipValue === savedValue ? 'true' : 'false'
          );
        }
      });
    };

    /**
     * Toggle aria-pressed on chip click.
     * Single-select: deselects all siblings. Multi-select: toggle only.
     */
    BenvelStylistFlow.prototype._onChipChange = function (e) {
      var chip = e.target.closest('.benvel-filter-chip');
      if (!chip) return;

      var chipGroup = chip.closest('[data-chip-group]');
      var isMulti = chipGroup && chipGroup.dataset.multiselect === 'true';

      if (isMulti) {
        var current = chip.getAttribute('aria-pressed') === 'true';
        chip.setAttribute('aria-pressed', current ? 'false' : 'true');
      } else {
        // Single-select: deselect siblings
        if (chipGroup) {
          chipGroup.querySelectorAll('.benvel-filter-chip').forEach(function (c) {
            c.setAttribute('aria-pressed', 'false');
          });
        }
        chip.setAttribute('aria-pressed', 'true');
      }
    };

    /**
     * Route click events to continue() or back() based on data-action.
     * Also handles chip toggling (chips fire as button clicks, not change events).
     */
    BenvelStylistFlow.prototype._onClick = function (e) {
      // Handle chip button clicks (they use type="button", not change events)
      var chip = e.target.closest('.benvel-filter-chip');
      if (chip) {
        this._onChipChange({ target: chip });
        return;
      }

      var actionEl = e.target.closest('[data-action]');
      if (!actionEl) return;

      var action = actionEl.dataset.action;
      if (action === 'continue') {
        e.preventDefault();
        this.continue();
      } else if (action === 'back') {
        e.preventDefault();
        this.back();
      } else if (action === 'close') {
        this._closeFlow();
      }
    };

    /**
     * Persist current step answers + advance to next step.
     * AC4: persists to sessionStorage[SESSION_KEY].
     */
    BenvelStylistFlow.prototype.continue = function () {
      this._persist();
      this._advance(+1);
    };

    /**
     * Persist current step answers + go back to previous step.
     * AC4: rehydrates previous step chip state from sessionStorage.
     */
    BenvelStylistFlow.prototype.back = function () {
      var stepEl = this.querySelector('[data-stylist-step]');
      var stepIndex = stepEl ? parseInt(stepEl.dataset.stepIndex, 10) : 1;

      if (stepIndex <= 1) {
        // Step 0 Back: close flow, return to intro modal
        this._closeFlow();
        return;
      }

      this._persist();
      this._advance(-1);
    };

    /**
     * Persist the current step's selected chip value(s) to sessionStorage.
     * Key: benvel.stylist.answers — JSON {[questionId]: value|value[]}
     */
    BenvelStylistFlow.prototype._persist = function () {
      var stepEl = this.querySelector('[data-stylist-step]');
      if (!stepEl) return;

      var questionId = stepEl.dataset.questionId;
      if (!questionId) return;

      var chipGroup = stepEl.querySelector('[data-chip-group]');
      var selectedValues = [];

      if (chipGroup) {
        chipGroup.querySelectorAll('.benvel-filter-chip[aria-pressed="true"]').forEach(
          function (chip) {
            selectedValues.push(chip.dataset.filterValue);
          }
        );
      }

      var raw = sessionStorage.getItem(BenvelStylistFlow.SESSION_KEY);
      var answers = {};
      try {
        answers = raw ? JSON.parse(raw) : {};
      } catch (e) {
        answers = {};
      }

      var isMulti = chipGroup && chipGroup.dataset.multiselect === 'true';
      answers[questionId] = isMulti ? selectedValues : selectedValues[0] || '';

      sessionStorage.setItem(BenvelStylistFlow.SESSION_KEY, JSON.stringify(answers));
    };

    /**
     * Advance (or retreat) the step counter by delta.
     * Updates sessionStorage[STEP_KEY] and dispatches a step-change event.
     * The host section (18.2 / the flow orchestrator section) listens for
     * this event and swaps the rendered step content.
     */
    BenvelStylistFlow.prototype._advance = function (delta) {
      var stepEl = this.querySelector('[data-stylist-step]');
      var currentStep = stepEl ? parseInt(stepEl.dataset.stepIndex, 10) : 1;
      var nextStep = Math.max(1, currentStep + delta);

      sessionStorage.setItem(BenvelStylistFlow.STEP_KEY, String(nextStep));

      this.dispatchEvent(
        new CustomEvent('benvel:stylist-step-change', {
          bubbles: true,
          detail: { from: currentStep, to: nextStep, delta: delta }
        })
      );
    };

    /**
     * Close the flow: dispatch close event (parent <dialog> handles it).
     * AC4: "On step 0 Back: closes the flow (returns control to 18.2 intro modal)."
     */
    BenvelStylistFlow.prototype._closeFlow = function () {
      this.dispatchEvent(
        new CustomEvent('benvel:stylist-step-close', {
          bubbles: true,
          detail: {}
        })
      );
    };

    /**
     * Respond to the intro modal start event (benvel:stylist-intro-start).
     * Advances from step 0 to step 1 when intro modal CTA is clicked.
     */
    BenvelStylistFlow.prototype._onIntroStart = function () {
      sessionStorage.setItem(BenvelStylistFlow.STEP_KEY, '1');
      this.dispatchEvent(
        new CustomEvent('benvel:stylist-step-change', {
          bubbles: true,
          detail: { from: 0, to: 1, delta: +1 }
        })
      );
    };

    return BenvelStylistFlow;
  })();

  // Register custom element if not already defined
  if (!customElements.get('benvel-stylist-flow')) {
    // Use Object.setPrototypeOf for compatibility with the function-based pattern
    Object.setPrototypeOf(BenvelStylistFlow.prototype, HTMLElement.prototype);
    Object.setPrototypeOf(BenvelStylistFlow, HTMLElement);
    customElements.define('benvel-stylist-flow', BenvelStylistFlow);
  }
})();

// === /stylist-step ===

// === stylist-loading ===

/**
 * benvel-stylist-flow.js — stylist-loading block
 * Story 18.4: Loading state (min 1500ms; never below 900ms)
 *
 * Responsibilities:
 * - Cycles status microcopy every 600ms (AC2).
 * - Provides a Promise that resolves after min 1500ms (AC3).
 * - Ensures a hard 900ms floor to avoid flicker.
 */
(function () {
  'use strict';

  // Duplicate-load guard
  if (document.documentElement.dataset.benvelStylistFlowLoading === 'loaded') {
    return;
  }
  document.documentElement.dataset.benvelStylistFlowLoading = 'loaded';

  /**
   * Manages the loading state UI cycling.
   * @param {HTMLElement} container - The .stylist-loading element.
   */
  function StylistLoadingController(container) {
    this.container = container;
    this.statusEl = container.querySelector('[data-stylist-loading-status]');
    this.currentIndex = 1;
    this.interval = null;
  }

  StylistLoadingController.prototype.start = function () {
    var self = this;
    if (!this.statusEl) return;

    this.interval = setInterval(function () {
      self.currentIndex++;
      if (self.currentIndex > 4) self.currentIndex = 1;

      var key = 'stylist.loading.status_' + self.currentIndex;
      // Note: In a real theme context, we would have a JS-side translation helper.
      // Since this is a vanilla snippet, we'll try to find the key in a global or
      // fallback to the pulsed mark + subtitle which carry the weight.
      // For Story 18.4, we use the pre-rendered status_1 and cycle it if the
      // server-side has emitted multiple keys into a JSON object.
      if (window.Benvel && window.Benvel.translations && window.Benvel.translations.stylist) {
        var msg = window.Benvel.translations.stylist.loading['status_' + self.currentIndex];
        if (msg) self.statusEl.textContent = msg;
      }
    }, 600);
  };

  StylistLoadingController.prototype.stop = function () {
    if (this.interval) clearInterval(this.interval);
  };

  /**
   * Returns a promise that resolves after the specified duration.
   * @param {number} ms
   * @returns {Promise}
   */
  window.stylistWait = function (ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  };

  /**
   * Initialize loading controller on element connect.
   */
  function initStylistLoading() {
    var loadingEl = document.querySelector('[data-stylist-loading]');
    if (loadingEl) {
      var controller = new StylistLoadingController(loadingEl);
      controller.start();
    }
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStylistLoading);
  } else {
    initStylistLoading();
  }
})();

// === /stylist-loading ===

// === stylist-results ===
/*
 * benvel-stylist-flow.js — stylist-results block
 * Story 18.5: AI Stylist Phase 2 results step
 *
 * Owns:
 *   - <benvel-stylist-results> custom element registration.
 *   - PreferenceSummary hydration from sessionStorage['benvel.stylist.answers']
 *     (AC4). Question labels read from the inline JSON blob the snippet emits.
 *   - "Añadir outfit completo" bulk-add (AC5). Single fetch POST to
 *     /cart/add.js with items[] array; on success per-card Añadido state
 *     (AC3 wiring re-use). Honors the no-JS form-POST fallback if JS is
 *     disabled (the surrounding <form action="/cart/add" method="POST">
 *     submits natively).
 *   - Per-card click delegation for quick-add → Añadido state (AC3).
 *   - cart:update listener: when the cart drawer / API reports a variant
 *     added that matches a slot variant, mark the slot card as Añadido
 *     (lifetime-of-page, no auto-revert per AC3 § graceful-MVP).
 *   - Per-piece click event dispatch (`benvel:stylist-piece-clicked`) so
 *     Story 18.6 swap-panel can listen.
 *
 * Constraints:
 *   - Reduced-motion respected (the CSS suppresses transitions; the JS
 *     simply toggles the class — no animation here).
 *   - Page must render without runtime JS errors per Story 1.18; every
 *     handler is try/catch-guarded.
 *   - sessionStorage is the SOLE source for buyer answers — never POSTed.
 */
(function () {
  'use strict';

  var SESSION_KEY = 'benvel.stylist.answers';
  var CART_ADD_URL = '/cart/add.js';

  /**
   * Read and parse the buyer answers blob from sessionStorage.
   * Returns {} on any error.
   */
  function readAnswers() {
    try {
      var raw = window.sessionStorage.getItem(SESSION_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  /**
   * Read the inline question-label map (rendered by stylist-results.liquid
   * as <script type="application/json" data-stylist-preference-labels>).
   * Returns {} on any error.
   */
  function readLabelMap(rootEl) {
    try {
      var node = rootEl.querySelector('[data-stylist-preference-labels]');
      if (!node) return {};
      var parsed = JSON.parse(node.textContent || '{}');
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  /**
   * Format a single answer value for display. Arrays → comma join;
   * objects → JSON stringify (defensive — answers SHOULD be primitive).
   */
  function formatValue(v) {
    if (v == null) return '';
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'object') {
      try { return JSON.stringify(v); } catch (_) { return ''; }
    }
    return String(v);
  }

  /**
   * BenvelStylistResults — custom element controller for the results step.
   * Mounts on <benvel-stylist-results> from snippets/stylist-results.liquid.
   *
   * Uses the same function-based custom-element pattern as
   * <benvel-stylist-flow> above (Object.setPrototypeOf at registration
   * time). Avoids the class syntax to stay aligned with the rest of this
   * asset bundle.
   */
  function BenvelStylistResults() {
    // Function-form constructor — wired to HTMLElement via setPrototypeOf
    // at registration time below. The custom-element runtime invokes this
    // as `new BenvelStylistResults()` only when the prototype chain is set
    // up correctly.
  }

  BenvelStylistResults.prototype.connectedCallback = function () {
    try {
      this._populatePreferenceSummary();
      this._bindClickDelegation();
      this._bindCartUpdate();
    } catch (e) {
      /* fail-soft per Story 1.18 invariant */
      if (window && window.console && console.warn) {
        console.warn('[benvel-stylist-results] init failed:', e);
      }
    }
  };

  BenvelStylistResults.prototype.disconnectedCallback = function () {
    try {
      if (this._cartUpdateHandler) {
        document.removeEventListener('cart:update', this._cartUpdateHandler);
        document.removeEventListener('cart:refresh', this._cartUpdateHandler);
      }
    } catch (_) {}
  };

  /**
   * AC4 — hydrate the PreferenceSummary <dl> from sessionStorage.
   */
  BenvelStylistResults.prototype._populatePreferenceSummary = function () {
    var root = this.querySelector('[data-stylist-preference-summary]');
    if (!root) return;

    var answers = readAnswers();
    var keys = Object.keys(answers);

    if (keys.length === 0) {
      var emptyEl = root.querySelector('[data-stylist-preference-empty]');
      if (emptyEl) emptyEl.removeAttribute('hidden');
      return;
    }

    var tmpl = root.querySelector('[data-stylist-preference-row]');
    if (!tmpl || !tmpl.content) return;

    var labelMap = readLabelMap(this);

    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      var node = tmpl.content.cloneNode(true);
      var labelEl = node.querySelector('[data-row-label]');
      var valueEl = node.querySelector('[data-row-value]');
      if (labelEl) labelEl.textContent = labelMap[key] || key;
      if (valueEl) valueEl.textContent = formatValue(answers[key]);
      root.appendChild(node);
    }
  };

  /**
   * Per-card click delegation:
   *   - [data-bulk-add] (sticky CTA bar) → bulk-add via fetch.
   *   - product-card clicks → dispatch `benvel:stylist-piece-clicked` for 18.6.
   *   - The per-card "Quick add" button (Horizon canonical) flows through
   *     the cart:update listener below to reach the Añadido state.
   */
  BenvelStylistResults.prototype._bindClickDelegation = function () {
    var self = this;
    this.addEventListener('click', function (e) {
      try {
        var bulkBtn = e.target && e.target.closest && e.target.closest('[data-bulk-add]');
        if (bulkBtn) {
          // Intercept only if fetch is available; otherwise let the native
          // form-POST submit (no-JS fallback survival).
          if (typeof window.fetch === 'function') {
            e.preventDefault();
            self._bulkAdd(bulkBtn);
          }
          return;
        }

        var slotEl = e.target && e.target.closest && e.target.closest('[data-stylist-outfit-slot]');
        if (slotEl) {
          // F5 fix (code-review 2026-05-27): exclude clicks that the
          // canonical product-card already handles (its own "Quick add"
          // button, the product image link, any submit-button inside the
          // card). Story 18.6 swap-panel listens for this event; without
          // this guard the buyer who clicks "Añadir" would see both the
          // cart drawer AND the swap-panel modal simultaneously.
          var interactive = e.target && e.target.closest && e.target.closest('a, button, [type="submit"], [data-action="add"]');
          if (interactive) {
            return;
          }
          var idx = slotEl.getAttribute('data-slot-index');
          var card = slotEl.querySelector('product-card, .product-card');
          var variantId = card ? card.getAttribute('data-variant-handle') : null;
          try {
            var ev = new CustomEvent('benvel:stylist-piece-clicked', {
              bubbles: true,
              detail: {
                slot_index: idx != null ? parseInt(idx, 10) : null,
                variant_id: variantId,
                source: 'stylist-results',
                timestamp_iso: new Date().toISOString()
              }
            });
            self.dispatchEvent(ev);
          } catch (_) {}
        }
      } catch (_) {
        /* fail-soft */
      }
    });
  };

  /**
   * cart:update listener — Horizon native event fires after every successful
   * /cart/add or /cart/change. We inspect the new cart for any variant ids
   * that match our slot cards and flip them to the Añadido state.
   */
  BenvelStylistResults.prototype._bindCartUpdate = function () {
    var self = this;
    this._cartUpdateHandler = function (e) {
      try {
        var detail = e && e.detail;
        var resource = detail && (detail.resource || detail.cart);
        if (resource && Array.isArray(resource.items)) {
          self._reconcileAddedSlotsFromCartItems(resource.items);
          return;
        }
        // Fallback — re-fetch cart state if the event detail did not include
        // it. Keep this defensive; do nothing if fetch is unavailable.
        if (typeof window.fetch === 'function') {
          window.fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (cart) {
              if (cart && Array.isArray(cart.items)) {
                self._reconcileAddedSlotsFromCartItems(cart.items);
              }
            })
            .catch(function () { /* fail-soft */ });
        }
      } catch (_) {
        /* fail-soft */
      }
    };
    document.addEventListener('cart:update', this._cartUpdateHandler);
    document.addEventListener('cart:refresh', this._cartUpdateHandler);
  };

  /**
   * Given a cart items[] array, mark any slot whose data-variant-handle
   * matches as Añadido.
   */
  BenvelStylistResults.prototype._reconcileAddedSlotsFromCartItems = function (items) {
    if (!items || !items.length) return;
    var variantIdSet = {};
    for (var i = 0; i < items.length; i++) {
      if (items[i] && items[i].variant_id != null) {
        variantIdSet[String(items[i].variant_id)] = true;
      } else if (items[i] && items[i].id != null) {
        // Some Horizon payloads use `id` for the variant id.
        variantIdSet[String(items[i].id)] = true;
      }
    }

    var slots = this.querySelectorAll('[data-stylist-outfit-slot]');
    for (var s = 0; s < slots.length; s++) {
      var slot = slots[s];
      var card = slot.querySelector('product-card, .product-card');
      if (!card) continue;
      var vid = card.getAttribute('data-variant-handle');
      if (vid && variantIdSet[String(vid)]) {
        this._markSlotAdded(slot);
      }
    }
  };

  /**
   * AC3 — toggle the Olive-soft Añadido visual state on a slot card.
   * Lifetime-of-page (no auto-revert). Idempotent.
   */
  BenvelStylistResults.prototype._markSlotAdded = function (slot) {
    if (!slot) return;
    var card = slot.querySelector('product-card, .product-card');
    if (!card) return;
    if (card.classList.contains('is-added')) return; // idempotent

    card.classList.add('is-added');

    var content = card.querySelector('.product-card__content');
    if (content) content.classList.add('is-added');

    // Optional checkmark flag — only inject if not already present.
    if (!card.querySelector('[data-bundle-added-flag]')) {
      try {
        var flag = document.createElement('span');
        flag.className = 'product-card__bundle-added-flag';
        flag.setAttribute('data-bundle-added-flag', '');
        flag.setAttribute('aria-live', 'polite');
        var label = (this.dataset && this.dataset.addedLabel) || 'Añadido';
        flag.textContent = label;
        (content || card).appendChild(flag);
      } catch (_) {}
    }
  };

  /**
   * AC5 — bulk-add the 3 outfit variants via /cart/add.js fetch.
   * On success: mark each slot Añadido + dispatch cart:update so the rest
   * of the theme (cart drawer, mini-cart count) refreshes.
   */
  BenvelStylistResults.prototype._bulkAdd = function (btn) {
    var self = this;
    var form = this.querySelector('#stylist-bulk-add-form');
    if (!form) return;

    var items = [];
    var hiddens = form.querySelectorAll('input[type="hidden"]');
    var byIndex = {};
    for (var h = 0; h < hiddens.length; h++) {
      var nm = hiddens[h].getAttribute('name') || '';
      // matches items[0][id] / items[0][quantity]
      var m = nm.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (m) {
        var idx = m[1];
        if (!byIndex[idx]) byIndex[idx] = {};
        byIndex[idx][m[2]] = hiddens[h].value;
      }
    }
    var idxKeys = Object.keys(byIndex).sort();
    for (var k = 0; k < idxKeys.length; k++) {
      var entry = byIndex[idxKeys[k]];
      if (entry && entry.id) {
        items.push({ id: parseInt(entry.id, 10), quantity: parseInt(entry.quantity || '1', 10) });
      }
    }
    if (items.length === 0) return;

    if (btn) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('disabled', '');
    }

    window.fetch(CART_ADD_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/add.js failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        // Mark all slots Añadido.
        var slots = self.querySelectorAll('[data-stylist-outfit-slot]');
        for (var s = 0; s < slots.length; s++) {
          self._markSlotAdded(slots[s]);
        }
        // Decision §D9 contract: clear guided-flow sessionStorage on a
        // successful bulk-add so the next guided-flow run in the same
        // browser tab starts from a clean PreferenceSummary. The two keys
        // are written by Story 18.3 (`benvel.stylist.answers`) + the host
        // step controller (`benvel.stylist.currentStep`). Fail-soft: a
        // sessionStorage write/read denial (third-party cookies blocked,
        // quota exceeded) MUST NOT regress the Añadido + cart:update UX.
        try {
          if (typeof window.sessionStorage !== 'undefined') {
            window.sessionStorage.removeItem('benvel.stylist.answers');
            window.sessionStorage.removeItem('benvel.stylist.currentStep');
          }
        } catch (_) { /* fail-soft */ }
        // Notify the rest of the theme.
        try {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { source: 'stylist-results-bulk-add' }
          }));
        } catch (_) {}
      })
      .catch(function (err) {
        if (window && window.console && console.warn) {
          console.warn('[benvel-stylist-results] bulk-add failed:', err);
        }
        // Allow user to retry — re-enable the button.
        if (btn) {
          btn.removeAttribute('aria-busy');
          btn.removeAttribute('disabled');
        }
      })
      .then(function () {
        if (btn) {
          btn.removeAttribute('aria-busy');
          // Leave the button disabled if all 3 slots were marked Añadido
          // (lifetime-of-page UX); otherwise re-enable for retry. We're
          // permissive here — re-enable always so a buyer can retry partial
          // failures from a stale cart state.
          btn.removeAttribute('disabled');
        }
      });
  };

  // Register the custom element (idempotent — guard against double-define).
  if (typeof window.customElements !== 'undefined' && !window.customElements.get('benvel-stylist-results')) {
    try {
      // Wire the function-based class to HTMLElement per the same pattern
      // as <benvel-stylist-flow> above.
      Object.setPrototypeOf(BenvelStylistResults.prototype, HTMLElement.prototype);
      Object.setPrototypeOf(BenvelStylistResults, HTMLElement);
      window.customElements.define('benvel-stylist-results', BenvelStylistResults);
    } catch (e) {
      if (window && window.console && console.warn) {
        console.warn('[benvel-stylist-results] customElements.define failed:', e);
      }
    }
  }
})();
// === /stylist-results ===

// === stylist-swap ===

/*
 * benvel-stylist-flow.js — stylist-swap block
 * Story 18.6: Swap panel — 3 alternatives per piece with "why it works"
 *
 * Owns:
 *   - Swap-trigger click delegation on [data-action="swap-trigger"].
 *   - Drawer open: clone the matching <template data-stylist-swap-rule-id>
 *     into the dialog body, then dialog.showModal().
 *   - AC3 availability post-filter — counts cloned <li> nodes. If < 3,
 *     dispatches `benvel:ai-slot-fallback` (Story 11.4a event-bus reuse)
 *     with `context: 'stylist-swap'` discriminator. Listens for response
 *     by checking siblings of higher data-stylist-swap-priority order.
 *   - Apply: writes the swapped piece to
 *     sessionStorage['benvel.stylist.composed-look'], dispatches
 *     `benvel:stylist-swap-applied`, closes drawer.
 *   - Cancel / Esc / backdrop / × close: dispatches
 *     `benvel:stylist-swap-close` without sessionStorage write.
 *
 * Constraints:
 *   - Page must render without runtime JS errors per Story 1.18; every
 *     handler is try/catch-guarded.
 *   - No-JS fallback: the trigger is a <button> inside the slot; if JS
 *     fails, the button click is a no-op and the drawer stays closed.
 *   - sessionStorage is the SOLE source for buyer composed-look state.
 *   - The `benvel:ai-slot-fallback` event NAME is owned by Story 11.4a —
 *     this controller DISPATCHES it; it does NOT define it.
 */
(function () {
  'use strict';

  // Duplicate-load guard — if a sibling block loads this bundle twice.
  if (document.documentElement.dataset.benvelStylistFlowSwap === 'loaded') {
    return;
  }
  document.documentElement.dataset.benvelStylistFlowSwap = 'loaded';

  var COMPOSED_LOOK_KEY = 'benvel.stylist.composed-look';
  var REQUESTED_COUNT = 3;

  /**
   * Read the composed-look from sessionStorage. Returns {} on any error.
   */
  function readComposedLook() {
    try {
      var raw = window.sessionStorage.getItem(COMPOSED_LOOK_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  /**
   * Write the composed-look back to sessionStorage. Fail-soft.
   */
  function writeComposedLook(state) {
    try {
      window.sessionStorage.setItem(COMPOSED_LOOK_KEY, JSON.stringify(state));
    } catch (_) {
      /* fail-soft per Story 1.18 invariant */
    }
  }

  /**
   * Initialize the swap drawer wiring once the DOM is ready.
   */
  function initStylistSwap() {
    var dlg = document.querySelector('[data-stylist-swap-panel]');
    if (!dlg) return;

    var body = dlg.querySelector('[data-stylist-swap-body]');
    var titleEl = dlg.querySelector('[data-stylist-swap-title]');
    var applyBtn = dlg.querySelector('[data-action="swap-apply"]');

    // Cached state for the currently-open swap session.
    var currentRuleId = null;
    var currentPieceRole = null;
    var currentSlotIndex = null;
    var fallbackTriedRules = {};

    /**
     * Locate a template by rule id. Returns null if missing.
     */
    function findTemplate(ruleId) {
      if (!ruleId) return null;
      return dlg.querySelector('template[data-stylist-swap-rule-id="' + cssEscape(ruleId) + '"]');
    }

    /**
     * Locate the next-highest-priority template that has NOT been tried
     * in this session. Used by the AC3 recompose path.
     */
    function findNextPriorityTemplate(currentPriority) {
      var templates = dlg.querySelectorAll('template[data-stylist-swap-rule-id]');
      var best = null;
      var bestPriority = Infinity;
      for (var i = 0; i < templates.length; i++) {
        var t = templates[i];
        var rid = t.getAttribute('data-stylist-swap-rule-id');
        if (fallbackTriedRules[rid]) continue;
        var p = parseInt(t.getAttribute('data-stylist-swap-priority') || '0', 10);
        // Strict next-priority: prefer higher (less-priority) numbers that
        // weren't tried, falling back to any untried template.
        if (p > currentPriority && p < bestPriority) {
          best = t;
          bestPriority = p;
        }
      }
      if (!best) {
        // No strict next-priority match — return any untried template.
        for (var j = 0; j < templates.length; j++) {
          var rid2 = templates[j].getAttribute('data-stylist-swap-rule-id');
          if (!fallbackTriedRules[rid2]) return templates[j];
        }
      }
      return best;
    }

    /**
     * Conservative CSS-attribute-value escape. Avoids relying on
     * CSS.escape (not universally available in older browsers).
     */
    function cssEscape(val) {
      return String(val).replace(/["\\]/g, '\\$&');
    }

    /**
     * Show the empty-state placeholder inside the drawer body. Used when
     * no rules are published OR when all recompose paths exhaust.
     */
    function showEmptyState() {
      if (!body) return;
      // Clear any cloned alternative list.
      var existingClone = body.querySelector('.stylist-swap-panel__condition');
      if (existingClone) existingClone.remove();
      var existingList = body.querySelector('.stylist-swap-panel__alternatives');
      if (existingList) existingList.remove();
      var emptyEl = body.querySelector('[data-stylist-swap-empty]');
      if (emptyEl) emptyEl.removeAttribute('hidden');
      // Apply stays disabled in the empty state.
      if (applyBtn) applyBtn.setAttribute('disabled', '');
    }

    /**
     * Hide the empty-state placeholder.
     */
    function hideEmptyState() {
      if (!body) return;
      var emptyEl = body.querySelector('[data-stylist-swap-empty]');
      if (emptyEl) emptyEl.setAttribute('hidden', '');
    }

    /**
     * Open the drawer for a given styling_rule id. Clones the matching
     * template into the body. Runs the AC3 availability post-filter (the
     * Liquid render-time filter already ran; this is the JS-side count
     * guard that fires the fallback event if the cloned list has < 3).
     */
    function openDrawerForRule(ruleId, pieceRole, slotIndex) {
      if (!body) return;
      currentRuleId = ruleId;
      currentPieceRole = pieceRole || '';
      currentSlotIndex = slotIndex != null ? slotIndex : null;
      // Clear any previous clone — keep the empty-state placeholder around.
      var prevCondition = body.querySelector('.stylist-swap-panel__condition');
      if (prevCondition) prevCondition.remove();
      var prevList = body.querySelector('.stylist-swap-panel__alternatives');
      if (prevList) prevList.remove();

      // Update title with piece-role interpolation.
      if (titleEl) {
        var tmpl = titleEl.getAttribute('data-title-template') || titleEl.textContent;
        if (currentPieceRole) {
          titleEl.textContent = tmpl.replace(/\{\{\s*piece_role\s*\}\}/g, currentPieceRole);
        } else {
          titleEl.textContent = tmpl.replace(/\{\{\s*piece_role\s*\}\}/g, '');
        }
      }

      var tpl = findTemplate(ruleId);
      if (!tpl || !tpl.content) {
        showEmptyState();
        return;
      }

      // Clone the template into the body.
      var clone = tpl.content.cloneNode(true);
      hideEmptyState();
      body.appendChild(clone);

      // AC3 — count rendered alternatives. The Liquid post-filter already
      // dropped variants where alt_product.available == false; this is a
      // defense-in-depth recount + fallback dispatch when the rendered set
      // is < 3 (e.g. BU shipped a rule with fewer than 3 alternatives).
      runAvailabilityFilter(ruleId);

      // Apply button stays disabled until a radio is selected.
      if (applyBtn) applyBtn.setAttribute('disabled', '');

      if (typeof dlg.showModal === 'function') {
        try {
          dlg.showModal();
        } catch (_) {
          // Some browsers throw if the dialog is already open. Fail-soft.
        }
      } else {
        // No <dialog> support — set the open attribute as a graceful fallback.
        dlg.setAttribute('open', '');
      }
    }

    /**
     * AC3 — count visible alternatives in the current body. If < 3,
     * dispatch `benvel:ai-slot-fallback` and attempt recompose from
     * next-priority rule (single attempt per click — no infinite loops).
     */
    function runAvailabilityFilter(ruleId) {
      var alts = body.querySelectorAll('.stylist-swap-panel__alternative');
      var availableCount = alts.length;

      if (availableCount >= REQUESTED_COUNT) return;

      // Mark this rule as tried so we don't loop back to it.
      fallbackTriedRules[ruleId] = true;

      // Dispatch the canonical fallback event.
      try {
        document.dispatchEvent(new CustomEvent('benvel:ai-slot-fallback', {
          bubbles: true,
          detail: {
            context: 'stylist-swap',
            styling_rule_id: ruleId,
            requested_count: REQUESTED_COUNT,
            available_count: availableCount,
            recompose_strategy: 'next-priority-rule'
          }
        }));
      } catch (_) { /* fail-soft */ }

      // Attempt recompose from next-priority rule.
      var curTpl = findTemplate(ruleId);
      var curPriority = curTpl
        ? parseInt(curTpl.getAttribute('data-stylist-swap-priority') || '0', 10)
        : 0;
      var nextTpl = findNextPriorityTemplate(curPriority);

      if (nextTpl) {
        var nextRid = nextTpl.getAttribute('data-stylist-swap-rule-id');
        // Re-open the drawer body content with the next rule's template.
        var prevCondition = body.querySelector('.stylist-swap-panel__condition');
        if (prevCondition) prevCondition.remove();
        var prevList = body.querySelector('.stylist-swap-panel__alternatives');
        if (prevList) prevList.remove();

        var nextClone = nextTpl.content.cloneNode(true);
        body.appendChild(nextClone);
        currentRuleId = nextRid;
        // Recurse the filter once on the new content (the tried-set guard
        // bounds the recursion to the published rule count).
        runAvailabilityFilter(nextRid);
      } else if (availableCount === 0) {
        // No further rules and the current rendering has zero alts —
        // show the empty-state branch.
        showEmptyState();
      }
      // Otherwise: 1 or 2 alternatives in the current body is acceptable
      // (defensive UX — show what we have rather than block the buyer).
    }

    /**
     * Apply the selected swap — write to sessionStorage, dispatch the
     * applied event, close the drawer.
     */
    function applySwap() {
      var checked = body && body.querySelector('input[type="radio"]:checked');
      if (!checked) return;

      var li = checked.closest('.stylist-swap-panel__alternative');
      if (!li) return;

      var swappedHandle = checked.value;
      var ruleId = li.getAttribute('data-styling-rule-id');
      var variantId = li.getAttribute('data-variant-id');

      var state = readComposedLook();
      if (!state.pieces || typeof state.pieces !== 'object') state.pieces = {};

      var roleKey = currentPieceRole || ('slot_' + (currentSlotIndex == null ? '0' : currentSlotIndex));
      var prev = state.pieces[roleKey] || {};
      state.pieces[roleKey] = {
        product_handle: swappedHandle,
        variant_id: variantId,
        styling_rule_id: ruleId,
        swapped_from: prev.product_handle || null,
        slot_index: currentSlotIndex,
        updated_at: new Date().toISOString()
      };
      writeComposedLook(state);

      try {
        document.dispatchEvent(new CustomEvent('benvel:stylist-swap-applied', {
          bubbles: true,
          detail: {
            slot_index: currentSlotIndex,
            piece_role: currentPieceRole,
            styling_rule_id: ruleId,
            product_handle: swappedHandle,
            variant_id: variantId
          }
        }));
      } catch (_) { /* fail-soft */ }

      closeDrawer('apply');
    }

    /**
     * Close the drawer + dispatch the close event. Does NOT write
     * sessionStorage.
     */
    function closeDrawer(reason) {
      try {
        if (typeof dlg.close === 'function') {
          dlg.close(reason || 'cancel');
        } else {
          dlg.removeAttribute('open');
        }
      } catch (_) { /* fail-soft */ }
      try {
        document.dispatchEvent(new CustomEvent('benvel:stylist-swap-close', {
          bubbles: true,
          detail: { reason: reason || 'cancel' }
        }));
      } catch (_) { /* fail-soft */ }
      // Reset per-session state.
      currentRuleId = null;
      currentPieceRole = null;
      currentSlotIndex = null;
      fallbackTriedRules = {};
    }

    /**
     * Click delegation switchboard — listens at the document level so we
     * pick up triggers anywhere on the results step.
     */
    function onDocumentClick(e) {
      try {
        var target = e.target;
        if (!target || !target.closest) return;

        // Trigger: "Cambiar pieza" button on an outfit slot.
        var trigger = target.closest('[data-action="swap-trigger"]');
        if (trigger) {
          e.preventDefault();
          var ruleId = trigger.getAttribute('data-styling-rule-id');
          var pieceRole = trigger.getAttribute('data-piece-role');
          var slotIndexRaw = trigger.getAttribute('data-slot-index');
          var slotIndex = slotIndexRaw != null ? parseInt(slotIndexRaw, 10) : null;
          fallbackTriedRules = {}; // fresh per-open session
          openDrawerForRule(ruleId, pieceRole, slotIndex);
          return;
        }

        // Drawer-internal actions — only handle when click is inside dialog.
        if (!dlg.contains(target)) return;

        var closeEl = target.closest('[data-action="swap-close"]');
        if (closeEl) {
          closeDrawer('close');
          return;
        }

        var cancelEl = target.closest('[data-action="swap-cancel"]');
        if (cancelEl) {
          closeDrawer('cancel');
          return;
        }

        var applyEl = target.closest('[data-action="swap-apply"]');
        if (applyEl) {
          applySwap();
          return;
        }

        // Backdrop click — clicking the dialog element directly lands on
        // the backdrop (the inner wrapper catches content clicks).
        if (target === dlg) {
          closeDrawer('backdrop');
          return;
        }
      } catch (_) { /* fail-soft per Story 1.18 */ }
    }

    /**
     * Radio change — enable the Apply button when any alt is selected.
     */
    function onDrawerChange(e) {
      try {
        if (!applyBtn) return;
        var t = e.target;
        if (t && t.matches && t.matches('input[type="radio"][name^="stylist-swap-"]:checked')) {
          applyBtn.removeAttribute('disabled');
        }
      } catch (_) { /* fail-soft */ }
    }

    /**
     * Esc key — native <dialog> handles this; we just sync the close
     * event dispatch on the `close` event.
     */
    function onDialogClose() {
      // Drawer closed via Esc or native close() — emit the close event
      // unless we already dispatched it via closeDrawer().
      try {
        if (currentRuleId !== null || currentPieceRole !== null) {
          document.dispatchEvent(new CustomEvent('benvel:stylist-swap-close', {
            bubbles: true,
            detail: { reason: 'native-close' }
          }));
        }
      } catch (_) { /* fail-soft */ }
      currentRuleId = null;
      currentPieceRole = null;
      currentSlotIndex = null;
      fallbackTriedRules = {};
    }

    document.addEventListener('click', onDocumentClick);
    dlg.addEventListener('change', onDrawerChange);
    dlg.addEventListener('close', onDialogClose);
  }

  // -------------------------------------------------------------------------
  // Boot: initialize when DOM is ready
  // -------------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStylistSwap);
  } else {
    initStylistSwap();
  }
})();
// === /stylist-swap ===

// === stylist-no-match ===
/**
 * Story 18.7 — No-match panel controller.
 *
 * Wires the Retry button (dispatches benvel:stylist-no-match-retry so
 * Story 18.3's step UI scaffold can re-open Step 1 with sessionStorage
 * answers preserved).
 *
 * Exposes window.benvel.stylist.noMatch.show() / hide() helpers so the
 * Story 18.5 results renderer can toggle the panel without coupling to
 * the DOM attribute directly.
 *
 * Consent-gate discipline (AC8): benvel:stylist-no-match-shown is optional
 * and emitted only when consent is present.
 */
(function () {
  'use strict';

  function initStylistNoMatch() {
    try {
      var panel = document.querySelector('[data-stylist-no-match]');
      if (!panel) return;

      // Wire Retry button — dispatches event for Story 18.3 listener.
      var retryBtn = panel.querySelector('[data-stylist-no-match-retry]');
      if (retryBtn) {
        retryBtn.addEventListener('click', function () {
          try {
            window.dispatchEvent(new CustomEvent('benvel:stylist-no-match-retry', {
              bubbles: false,
              detail: { timestamp_iso: new Date().toISOString() }
            }));
          } catch (_) { /* fail-soft per Story 1.18 */ }
        });
      }

      // Public helpers for Story 18.5's results renderer.
      function show() {
        try {
          panel.hidden = false;
          panel.setAttribute('aria-live', 'polite');
          if (
            window.benvel &&
            window.benvel.consent &&
            typeof window.benvel.consent.has === 'function' &&
            window.benvel.consent.has('analytics')
          ) {
            window.dispatchEvent(new CustomEvent('benvel:stylist-no-match-shown', {
              bubbles: false,
              detail: { timestamp_iso: new Date().toISOString() }
            }));
          }
        } catch (_) { /* fail-soft */ }
      }

      function hide() {
        try {
          panel.hidden = true;
        } catch (_) { /* fail-soft */ }
      }

      // Mount helpers onto window.benvel namespace (create if absent).
      try {
        window.benvel = window.benvel || {};
        window.benvel.stylist = window.benvel.stylist || {};
        window.benvel.stylist.noMatch = { show: show, hide: hide };
      } catch (_) { /* fail-soft */ }

      // Also respond to the benvel:stylist-no-match event emitted by the
      // results snippet empty-shell branch (Story 18.5 graceful degradation).
      document.addEventListener('benvel:stylist-no-match', function () {
        try { show(); } catch (_) {}
      });

    } catch (_) { /* fail-soft per Story 1.18 */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStylistNoMatch);
  } else {
    initStylistNoMatch();
  }
})();
// === /stylist-no-match ===

// === whatsapp-stylist-handoff ===
/**
 * Story 18.7 — WhatsApp Stylist handoff dialog controller.
 *
 * Hard invariant (AC3): window.open(wa.me/...) appears ONLY inside the
 * data-handoff-send click handler — never on open, toggle change, cancel,
 * close (X), ESC, or scrim click.
 *
 * Consent-gate discipline (AC8): benvel:stylist-whatsapp-handoff-opened
 * (optional) and benvel:stylist-whatsapp-handoff-sent are emitted only
 * when window.benvel.consent.has('analytics') is truthy.
 */
(function () {
  'use strict';

  function initWhatsappHandoff() {
    try {
      var dlg = document.getElementById('benvel-whatsapp-stylist-handoff');
      if (!dlg) return;

      // ---- Locale strings -----------------------------------------------
      var localeData = {};
      try {
        var localeEl = dlg.querySelector('[data-stylist-handoff-locale]');
        if (localeEl) {
          localeData = JSON.parse(localeEl.textContent);
        }
      } catch (_) { /* fall through — use empty strings gracefully */ }

      var templateWithLook    = localeData.with_look    || '';
      var templateWithoutLook = localeData.without_look || '';
      var templateBullet      = localeData.bullet       || '• {role} — {title} — talla {size}';
      var noLookYetCopy       = localeData.no_look_yet  || '';

      // ---- Helpers --------------------------------------------------------
      function hasAnalyticsConsent() {
        try {
          return (
            window.benvel &&
            window.benvel.consent &&
            typeof window.benvel.consent.has === 'function' &&
            window.benvel.consent.has('analytics')
          );
        } catch (_) { return false; }
      }

      function readComposition() {
        try {
          var raw = sessionStorage.getItem('benvel.stylist.composition');
          if (!raw) return null;
          return JSON.parse(raw);
        } catch (_) { return null; }
      }

      function buildBulletLine(item) {
        try {
          return templateBullet
            .replace('{role}',  item.role  || '')
            .replace('{title}', item.title || item.product_handle || '')
            .replace('{size}',  item.size  || '');
        } catch (_) { return ''; }
      }

      function buildMessageText(includeLook, composition) {
        try {
          if (includeLook && composition && Array.isArray(composition.items) && composition.items.length > 0) {
            var bullets = composition.items.map(buildBulletLine).join('\n');
            return templateWithLook.replace('{items}', bullets);
          }
          return templateWithoutLook;
        } catch (_) { return templateWithoutLook; }
      }

      function updatePreview() {
        try {
          var previewEl = dlg.querySelector('[data-handoff-preview]');
          if (!previewEl) return;
          var toggleEl = dlg.querySelector('[data-handoff-include-look]');
          var includeLook = toggleEl ? toggleEl.checked : false;
          var composition = readComposition();
          // When source is no_match and toggle is disabled, show helper copy.
          if (dlg.dataset.handoffCurrentSource === 'no_match' && toggleEl && toggleEl.disabled) {
            previewEl.textContent = templateWithoutLook;
          } else {
            previewEl.textContent = buildMessageText(includeLook, composition);
          }
        } catch (_) { /* fail-soft */ }
      }

      function openDialog(sourceAttr) {
        try {
          // Record current source for downstream handlers.
          dlg.dataset.handoffCurrentSource = sourceAttr || 'unknown';

          var toggleEl = dlg.querySelector('[data-handoff-include-look]');
          if (toggleEl) {
            if (sourceAttr === 'no_match') {
              // Toggle defaults OFF + disabled when no composed look exists.
              var composition = readComposition();
              var hasComposition = composition && Array.isArray(composition.items) && composition.items.length > 0;
              toggleEl.checked = false;
              toggleEl.disabled = !hasComposition;
              if (!hasComposition && noLookYetCopy) {
                // Surface helper text via aria-description if available.
                toggleEl.setAttribute('aria-description', noLookYetCopy);
              }
            } else {
              // Results source — toggle ON by default.
              toggleEl.checked = true;
              toggleEl.disabled = false;
              toggleEl.removeAttribute('aria-description');
            }
          }

          updatePreview();
          dlg.showModal();

          // Initial focus on the preview so screen readers announce it.
          var previewEl = dlg.querySelector('[data-handoff-preview]');
          if (previewEl) { previewEl.focus(); }

          // Optional analytics (consent-gated).
          if (hasAnalyticsConsent()) {
            try {
              window.dispatchEvent(new CustomEvent('benvel:stylist-whatsapp-handoff-opened', {
                bubbles: false,
                detail: { source: sourceAttr || 'unknown', timestamp_iso: new Date().toISOString() }
              }));
            } catch (_) {}
          }

        } catch (_) { /* fail-soft */ }
      }

      // ---- Wire: every [data-stylist-handoff-open] trigger on the page ---
      document.addEventListener('click', function (e) {
        try {
          var trigger = e.target && e.target.closest
            ? e.target.closest('[data-stylist-handoff-open]')
            : null;
          if (!trigger) return;
          var source = trigger.dataset.handoffSource || 'unknown';
          openDialog(source);
        } catch (_) { /* fail-soft */ }
      });

      // ---- Wire: include-look toggle ------------------------------------
      var toggleInput = dlg.querySelector('[data-handoff-include-look]');
      if (toggleInput) {
        toggleInput.addEventListener('change', function () {
          try { updatePreview(); } catch (_) {}
        });
      }

      // ---- Wire: Cancel -------------------------------------------------
      var cancelBtn = dlg.querySelector('[data-handoff-cancel]');
      if (cancelBtn) {
        cancelBtn.addEventListener('click', function () {
          try { dlg.close('cancel'); } catch (_) {}
        });
      }

      // ---- Wire: Close (X) ---------------------------------------------
      var closeBtn = dlg.querySelector('[data-handoff-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          try { dlg.close('close'); } catch (_) {}
        });
      }

      // ---- Wire: Scrim click (dialog element itself) --------------------
      dlg.addEventListener('click', function (e) {
        try {
          if (e.target === dlg) { dlg.close('scrim'); }
        } catch (_) {}
      });

      // ---- Wire: Send (THE critical path — AC3 hard invariant) ---------
      // window.open() appears ONLY here. Never anywhere else in this file.
      var sendBtn = dlg.querySelector('[data-handoff-send]');
      if (sendBtn) {
        sendBtn.addEventListener('click', function () {
          try {
            var text = '';
            var previewEl = dlg.querySelector('[data-handoff-preview]');
            if (previewEl) { text = previewEl.textContent.trim(); }

            var number = dlg.dataset.handoffWhatsappNumber || '';
            if (!number) return; // Guard: no number, no send.

            var url = 'https://wa.me/' + number + '?text=' + encodeURIComponent(text);
            dlg.close('send');
            window.open(url, '_blank', 'noopener,noreferrer');

            if (hasAnalyticsConsent()) {
              window.dispatchEvent(new CustomEvent('benvel:stylist-whatsapp-handoff-sent', {
                bubbles: false,
                detail: {
                  source: dlg.dataset.handoffCurrentSource || 'unknown',
                  timestamp_iso: new Date().toISOString()
                }
              }));
            }
          } catch (_) { /* fail-soft */ }
        });
      }

    } catch (_) { /* fail-soft per Story 1.18 */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhatsappHandoff);
  } else {
    initWhatsappHandoff();
  }
})();
// === /whatsapp-stylist-handoff ===

// === stylist-save-look ===
/**
 * Story 18.7 — Save-look CTA on the Stylist results step.
 *
 * On click:
 *   1. Reads composition from sessionStorage["benvel.stylist.composition"].
 *   2. Reads existing looks from localStorage["benvel.wishlist.looks"]
 *      (raw parse per Decision §D5 — no public helper exposed at dispatch).
 *   3. Checks idempotency (sorted+stringified items comparison).
 *   4. Appends new look object + writes back to localStorage.
 *   5. Dispatches benvel:wishlist-look-saved (NOT consent-gated — state change).
 *   6. If consent present, dispatches benvel:wishlist-look-saved-from-stylist.
 *   7. Flips CTA to "Guardado" state (disabled + olive-soft style).
 *
 * On mount, pre-flips to "Guardado" if the current composition is already
 * in localStorage (idempotency on page reload).
 *
 * Consent-gate discipline (AC8):
 *   - localStorage write is NOT consent-gated (functional storage).
 *   - benvel:wishlist-look-saved is NOT consent-gated (state-change, matches
 *     Story 14.1 heart-button precedent).
 *   - benvel:wishlist-look-saved-from-stylist IS consent-gated (analytics).
 */
(function () {
  'use strict';

  var LOOKS_KEY       = 'benvel.wishlist.looks';
  var COMP_KEY        = 'benvel.stylist.composition';
  var SAVED_CLASS     = 'stylist-results__save-look--saved';

  // ---- Tiny UUID v4 (no dependency) ----------------------------------------
  function uuid4() {
    try {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      // Fallback polyfill.
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
    } catch (_) { return Date.now().toString(36); }
  }

  // ---- Helpers ---------------------------------------------------------------
  function hasAnalyticsConsent() {
    try {
      return (
        window.benvel &&
        window.benvel.consent &&
        typeof window.benvel.consent.has === 'function' &&
        window.benvel.consent.has('analytics')
      );
    } catch (_) { return false; }
  }

  function readComposition() {
    try {
      var raw = sessionStorage.getItem(COMP_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) { return null; }
  }

  function readLooks() {
    try {
      var raw = localStorage.getItem(LOOKS_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  }

  function writeLooks(looks) {
    try {
      localStorage.setItem(LOOKS_KEY, JSON.stringify(looks));
    } catch (_) { /* fail-soft */ }
  }

  function compositionKey(composition) {
    try {
      if (!composition || !Array.isArray(composition.items)) return null;
      var sorted = composition.items.slice().sort(function (a, b) {
        var ka = String(a.variant_id || a.product_handle || '');
        var kb = String(b.variant_id || b.product_handle || '');
        return ka < kb ? -1 : ka > kb ? 1 : 0;
      });
      return JSON.stringify(sorted);
    } catch (_) { return null; }
  }

  function isDuplicate(composition, looks) {
    try {
      var key = compositionKey(composition);
      if (!key) return false;
      return looks.some(function (look) {
        var c = { items: look.items };
        return compositionKey(c) === key;
      });
    } catch (_) { return false; }
  }

  function flipToSaved(cta, savedLabel) {
    try {
      cta.setAttribute('disabled', '');
      cta.setAttribute('aria-disabled', 'true');
      cta.classList.add(SAVED_CLASS);
      var labelSpan = cta.querySelector('.stylist-results__save-look-label');
      if (labelSpan) {
        labelSpan.textContent = savedLabel || cta.dataset.savedLabel || 'Guardado';
      }
    } catch (_) { /* fail-soft */ }
  }

  // ---- Main init -----------------------------------------------------------
  function initStylistSaveLook() {
    try {
      var cta = document.querySelector('[data-stylist-save-look]');
      if (!cta) return;

      var savedLabel = cta.dataset.savedLabel || 'Guardado';
      var errorLabel = cta.dataset.errorLabel  || 'No se pudo guardar el look';

      // Pre-flip on mount if composition already saved.
      var composition = readComposition();
      if (composition) {
        var looks = readLooks();
        if (isDuplicate(composition, looks)) {
          flipToSaved(cta, savedLabel);
          return; // Already saved — no click handler needed.
        }
      }

      cta.addEventListener('click', function () {
        try {
          composition = readComposition();

          if (!composition || !Array.isArray(composition.items) || composition.items.length === 0) {
            // Surface inline error copy via aria-live on the CTA.
            var labelSpan = cta.querySelector('.stylist-results__save-look-label');
            if (labelSpan) { labelSpan.textContent = errorLabel; }
            cta.setAttribute('aria-live', 'polite');
            return;
          }

          var currentLooks = readLooks();

          if (isDuplicate(composition, currentLooks)) {
            // Already saved — flip CTA anyway without re-appending.
            flipToSaved(cta, savedLabel);
            return;
          }

          // Build the new look object (Story 14.2 / 14.4 schema).
          var newLook = {
            id: uuid4(),
            name: composition.title || 'Look del Stylist',
            items: composition.items.map(function (item) {
              return {
                variant_id:     item.variant_id     || null,
                product_handle: item.product_handle || '',
                role:           item.role           || ''
              };
            }),
            created_at: new Date().toISOString(),
            source: 'stylist'
          };

          currentLooks.push(newLook);
          writeLooks(currentLooks);

          // State-change event — NOT consent-gated (matches Story 14.1 precedent).
          window.dispatchEvent(new CustomEvent('benvel:wishlist-look-saved', {
            bubbles: false,
            detail: { source: 'stylist', look_id: newLook.id, timestamp_iso: newLook.created_at }
          }));

          // Analytics event — consent-gated.
          if (hasAnalyticsConsent()) {
            window.dispatchEvent(new CustomEvent('benvel:wishlist-look-saved-from-stylist', {
              bubbles: false,
              detail: { look_id: newLook.id, item_count: newLook.items.length, timestamp_iso: newLook.created_at }
            }));
          }

          flipToSaved(cta, savedLabel);

        } catch (_) {
          // Fail-soft: surface error copy to buyer.
          try {
            var sp = cta.querySelector('.stylist-results__save-look-label');
            if (sp) { sp.textContent = errorLabel; }
            cta.setAttribute('aria-live', 'polite');
          } catch (__) { /* give up */ }
        }
      });

    } catch (_) { /* fail-soft per Story 1.18 */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStylistSaveLook);
  } else {
    initStylistSaveLook();
  }
})();
// === /stylist-save-look ===
